import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthResponse, CurrentUser, Role, User } from '../models/user';
import { FeatureService } from '../features/feature.service';
import { CrudService } from '../services/crud.service';
import { AuthStatus } from './auth-service.constants';
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly profileUpdated$ = new Subject<void>();
  private readonly authenticationChanges = new Subject<boolean>();
  readonly authenticationChanges$ = this.authenticationChanges.asObservable();
  private readonly currentUserState = signal<CurrentUser | null>(null);
  private readonly authStatusState = signal<AuthStatus>(AuthStatus.Loading);
  private restoreInProgress: Observable<boolean> | null = null;
  private sessionRestoreSuppressed = false;
  private authChannel?: BroadcastChannel;

  readonly currentUser = this.currentUserState.asReadonly();
  readonly authStatus = this.authStatusState.asReadonly();
  readonly isAuthenticated = computed(() => this.authStatusState() === AuthStatus.Authenticated);
  readonly role = computed(() => this.currentUserState()?.role ?? null);
  readonly userId = computed(() => this.currentUserState()?.userId ?? null);
  readonly profileId = computed(() => this.currentUserState()?.profileId ?? null);
  readonly userName = computed(() => this.currentUserState()?.userName ?? null);
  readonly isAdmin = computed(() => this.role() === Role.Admin);
  readonly isTeacher = computed(() => this.role() === Role.Teacher);
  readonly isStudent = computed(() => this.role() === Role.Student);

  constructor(
    private readonly router: Router,
    private readonly crud: CrudService,
    private readonly featureService: FeatureService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
    if (isPlatformBrowser(this.platformId) && 'BroadcastChannel' in window) {
      this.authChannel = new BroadcastChannel('praband-auth');
      this.authChannel.addEventListener('message', (event) => {
        if (event.data === 'LOGOUT') {
          this.performLocalLogout(false);
        } else if (event.data === 'SESSION_CHANGED') {
          this.restoreSession(true).subscribe();
        }
      });
    }
  }

  onProfileUpdated(): Observable<void> {
    return this.profileUpdated$.asObservable();
  }

  notifyProfileUpdated(): void {
    this.profileUpdated$.next();
  }

  authenticateUser(identifier: string, password: string): Observable<AuthResponse> {
    const normalizedIdentifier = identifier.trim();
    return this.crud
      .post('auth/login', { identifier: normalizedIdentifier, password }, { withCredentials: true })
      .pipe(
        tap((response: AuthResponse) => {
          if (!response?.user) {
            throw new Error('Invalid login response from server');
          }
          this.setAuthenticatedUser(response.user);
          this.authChannel?.postMessage('SESSION_CHANGED');
        }),
      );
  }

  restoreSession(force = false): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(false);
    }
    if (!force && this.authStatusState() === AuthStatus.Authenticated) {
      return of(true);
    }
    if (
      !force &&
      this.authStatusState() === AuthStatus.Unauthenticated &&
      this.sessionRestoreSuppressed
    ) {
      return of(false);
    }
    if (this.restoreInProgress) {
      return this.restoreInProgress;
    }

    this.authStatusState.set(AuthStatus.Loading);
    this.restoreInProgress = this.crud.get('auth/current-user').pipe(
      tap((user: CurrentUser) => this.setAuthenticatedUser(user)),
      map(() => true),
      catchError((error: unknown) => {
        const isAuthorizationFailure =
          error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
        this.clearAuthState(isAuthorizationFailure);
        return of(false);
      }),
      finalize(() => (this.restoreInProgress = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.restoreInProgress;
  }

  refreshSession(): Observable<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new Error('Session refresh requires a browser'));
    }
    return from(this.refreshAcrossTabs());
  }

  logout(): void {
    this.crud
      .post('auth/logout', undefined, { withCredentials: true })
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => {
        this.authChannel?.postMessage('LOGOUT');
        this.performLocalLogout(false);
      });
  }

  performLocalLogout(broadcast = false): void {
    this.clearAuthState(true);
    this.clearSelectedCourseId();
    if (broadcast) {
      this.authChannel?.postMessage('LOGOUT');
    }
    if (this.router.url !== '/login') {
      void this.router.navigate(['/login']);
    }
  }

  fetchUserData(): Observable<User | null> {
    const userId = this.userId();
    return userId ? this.crud.get(`users/${userId}`) : of(null);
  }

  getSelectedCourseId(): string | null {
    return isPlatformBrowser(this.platformId) ? sessionStorage.getItem('selectedCourseId') : null;
  }

  setSelectedCourseId(courseId: string): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('selectedCourseId', courseId);
    }
  }

  clearSelectedCourseId(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('selectedCourseId');
    }
  }

  fetchCurrentTeacherProfile(): Observable<any> {
    return this.crud.get('teacher/profile/current-user');
  }

  fetchCurrentStudentProfile(): Observable<any> {
    return this.crud.get('students/profile/current-user');
  }

  fetchCurrentAdminProfile(): Observable<any> {
    return this.crud.get('admins/profile/current-user');
  }

  private setAuthenticatedUser(user: CurrentUser): void {
    this.sessionRestoreSuppressed = false;
    this.currentUserState.set(user);
    this.authStatusState.set(AuthStatus.Authenticated);
    this.authenticationChanges.next(true);
  }

  private clearAuthState(suppressRestore = false): void {
    this.sessionRestoreSuppressed ||= suppressRestore;
    this.featureService.reset();
    this.currentUserState.set(null);
    this.authStatusState.set(AuthStatus.Unauthenticated);
    this.authenticationChanges.next(false);
  }

  private async refreshAcrossTabs(): Promise<void> {
    const refresh = async (): Promise<void> => {
      const currentUser = await this.fetchCurrentUserDirect();
      if (currentUser) {
        this.setAuthenticatedUser(currentUser);
        return;
      }

      const refreshResponse = await fetch(`${environment.apiUrl}auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!refreshResponse.ok) {
        throw new Error('Session refresh failed');
      }

      const restoredUser = await this.fetchCurrentUserDirect();
      if (!restoredUser) {
        throw new Error('Unable to restore refreshed session');
      }
      this.setAuthenticatedUser(restoredUser);
      this.authChannel?.postMessage('SESSION_CHANGED');
    };

    if ('locks' in navigator) {
      await navigator.locks.request('praband-auth-refresh', refresh);
      return;
    }
    await refresh();
  }

  /** Bypasses HttpClient so an interceptor-triggered refresh cannot intercept itself. */
  private async fetchCurrentUserDirect(): Promise<CurrentUser | null> {
    const response = await fetch(`${environment.apiUrl}auth/current-user`, {
      credentials: 'include',
    });
    return response.ok ? ((await response.json()) as CurrentUser) : null;
  }
}
